/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import qs from 'qs';
import {
  fromBooleanString,
  isBooleanString
} from './BooleanUtils';

export const DEFAULT_QUERY_PARAMS = ['env', 'oidcEnabled', 'features'];
/**
 * Provides a utility for safely retrieving the (raw) application query string.
 *
 * ```typescript
 *   import {getQueryString} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?debug=true&userId=4321&accountName=Some%20Account
 *   const search = getQueryString(); // ?debug=true&userId=4321&accountName=Some%20Account
 * ```
 *
 * @returns
 */
export const getQueryString = (): string => (
  globalThis?.location?.search
);

/**
 * Provides a utility which allows for retrieving a specific parameter from the
 * application query string. By default, this function will return the value of
 * the specified parameter. If the `pair` argument is set to true, both the key
 * and value pair for the query parameter will be returned as an object.
 *
 * ```typescript
 *   import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?debug=true&userId=4321&accountName=Some%20Account
 *   const search = getParameter(debug); // true
 *   const search = getParameter(debug, true); // {debug: true}
 * ```
 *
 * @returns
 */
export const getParameter = (param: string, pair = false) => {
  const parsed = (qs.parse(getQueryString(), {ignoreQueryPrefix: true}) || {});
  if (param) {
    let value: any = parsed[param];
    if (!isBooleanString(value)) {
      if (!isNaN(value)) {
        value = Number(value as string);
      }
    } else {
      value = fromBooleanString(value as string);
    }
    return pair ? {[param]: value} : value;
  }
};

/**
 * Provides a utility which allows for retrieving a default parameters from the
 * application query string.
 *
 * ```typescript
 *   import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   // https://app.calamp.com?env=us&debug=true&userId=4321&accountName=Some%20Account
 *   const search = getDefaultParameters(); // {env: 'us'}
 * ```
 *
 * @returns
 */
export const getDefaultParameters = () => {
  const results = {};
  for (const param of DEFAULT_QUERY_PARAMS) {
    const result = getParameter(param, true);
    const value = result[param];
    if (value) {
      Object.assign(results, result);
    }
  }
  return results;
};

/**
 * Provides a utility which removes the query string from the given url (if
 * present), and returns the resulting url with the query string removed.
 *
 * ```typescript
 *   import {removeQueryString} from '@ion/core/src/utils/QueryStringUtils';
 *
 *   const uri = 'https://admin.calamp.com?env=us&debug=true&features=apm,autojobs';
 *   const url = removeQueryString(uri); // https://admin.calamp.com
 * ```
 *
 * @param   url The url for which the query string is to be removed (if included).
 * @returns The resulting url with the query string removed
 */
export const removeQueryString = (url: string): string => {
  const qs = url.indexOf('?');
  if (qs > -1) {
    return url.substring(0, qs);
  }
  return url;
};
